import { useContext, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FIRST_PAGE } from 'data/global';
import useInvalidateQueryOnUnmount from 'hooks/useInvalidateQueryOnUnmount';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import usePageParams from 'hooks/usePageParams';
import useQueryFilter from 'hooks/useQueryFilter';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomReport } from '../types';
import { PaginatedData } from 'models/PaginatedData';
import { safeParse, safeStringify } from 'utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

type UseReportsQueryParams = {
  dataRoomId: string;
  isSummarised?: boolean;
};

const REPORTS_QUERY_KEY = 'data-room-reports';

export default function useReportsQuery({
  dataRoomId,
  isSummarised = false,
}: UseReportsQueryParams) {
  const { popServerError } = useContext(PopNotificationsContext);

  const page = usePageParams();
  const { limit } = useNumberOfResultsPerPage();

  const {
    params: { action, dataRoomMemberId, from, to, qaAction },
    hasActiveFilter,
    applyQueryFilter,
  } = useQueryFilter();

  const queryKey = [
    REPORTS_QUERY_KEY,
    {
      action,
      dataRoomId,
      dataRoomMemberId,
      from,
      isSummarised,
      limit,
      page,
      qaAction,
      to,
    },
  ];

  const { data, isLoading } = useQuery<PaginatedData<DataRoomReport>>({
    queryKey,
    queryFn: async () => {
      try {
        const parsedAction = safeParse(action) || [];
        const parsedQaAction = safeParse(qaAction) || [];

        const { data } = await dataRoomApi.reports.getReports(dataRoomId, {
          $page: page,
          $limit: limit,
          action:
            parsedAction.length || parsedQaAction.length
              ? safeStringify([...parsedAction, ...parsedQaAction])
              : null,
          dataRoomMemberId,
          createdAt: from || to ? { $gte: from, $lte: to } : null,
          isSummarised,
        });

        return data;
      } catch (error) {
        popServerError(error);
      }
    },
    enabled: !!dataRoomId,
  });

  const totalPages = data?.totalPages || 0;
  const reports = data?.items || [];

  useEffect(() => {
    if (totalPages && +page > +FIRST_PAGE && +page > totalPages) {
      applyQueryFilter('page', FIRST_PAGE);
    }
  }, [page, totalPages, applyQueryFilter]);

  useInvalidateQueryOnUnmount(REPORTS_QUERY_KEY);

  return {
    hasActiveFilter,
    isLoading,
    totalPages,
    reports,
  };
}
